import { message } from 'antd'
import CryptoJS from 'crypto-js'
import { isNil, isPlainObject, isString, throttle } from 'lodash'

import { getAuthStoreInitialValue } from '@eastcoal/auth/store/inititalValue'
import { listenLoginEvent } from '@eastcoal/auth/utils/event'
import { DeviceType } from '@eastcoal/shared/enums/DeviceType'

import { system } from '../../config/system'
import {
  getApiHint,
  getCipherConfig,
  isEnableCipher
} from '../../store/application/helpers/util'
import { withinWhitelist } from '../browser'
import { gotoLogin } from '../route'

import { createRequest } from './core'
import { ApiConfig, RequestOptions, RequestHeaders } from './interface'
import { parseResult } from './parseResult'

let loginDetail = getAuthStoreInitialValue()

listenLoginEvent(store => {
  loginDetail = store
})

const setHeaderIfUnset = (
  headers: RequestHeaders,
  header: string,
  value?: any
) => {
  if (isNil(headers[header])) {
    headers[header] = value
  }
}

const requestHasBody = (method: string) => {
  return !['GET', 'HEAD'].includes(method.toUpperCase())
}

// 加密请求体
const entryptRequestBody = (options: RequestOptions<ApiConfig>) => {
  const { method, data, headers = {} } = options
  if (requestHasBody(method)) {
    // 上传附件时让浏览器自动设置 Content-Type
    if (data instanceof FormData) {
      delete headers['Content-Type']
    }
    const cipherEnabled = isEnableCipher()
    const cipherConfig = getCipherConfig()
    const cipherSecret = cipherConfig.secret
    const cipherIv = cipherConfig.iv
    if (cipherEnabled && cipherSecret && cipherIv) {
      const encryptData = (data: string) => {
        return CryptoJS.AES.encrypt(
          data,
          CryptoJS.enc.Base64.parse(cipherSecret),
          {
            iv: CryptoJS.enc.Base64.parse(cipherIv),
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
          }
        ).ciphertext.toString(CryptoJS.enc.Base64)
      }
      // 参数加密
      if (isString(data) || data instanceof URLSearchParams) {
        options.data = encryptData(data.toString())
        setHeaderIfUnset(headers, 'Content-Type', 'text/plain;charset=utf-8')
        setHeaderIfUnset(
          headers,
          'X-Decrypt-Data-Type',
          'x-www-form-urlencoded'
        )
      } else if (isPlainObject(data)) {
        options.data = encryptData(JSON.stringify(data))
        setHeaderIfUnset(headers, 'Content-Type', 'text/plain;charset=utf-8')
        setHeaderIfUnset(headers, 'X-Decrypt-Data-Type', 'json')
      }
    } else {
      if (cipherEnabled === false) {
        setHeaderIfUnset(headers, 'X-Decrypt-Enabled', 'false')
      }
      // 完善请求头信息
      if (isString(data) || data instanceof URLSearchParams) {
        setHeaderIfUnset(
          headers,
          'Content-Type',
          'application/x-www-form-urlencoded;charset=utf-8'
        )
      } else if (isPlainObject(data)) {
        setHeaderIfUnset(
          headers,
          'Content-Type',
          'application/json;charset=utf-8'
        )
      }
    }
  }
}

const transformRequest = (
  options: RequestOptions<ApiConfig>
): RequestOptions<ApiConfig> => {
  if (!options.headers) {
    options.headers = {}
  }
  entryptRequestBody(options)
  // 访问 hint 指定的服务
  const hint = getApiHint()
  setHeaderIfUnset(options.headers, hint.key, hint.value)
  // 设置应用信息
  setHeaderIfUnset(options.headers, 'X-Application-Device', DeviceType.PC)
  setHeaderIfUnset(options.headers, 'X-Application-Type', system.env.appType)
  setHeaderIfUnset(
    options.headers,
    'X-Application-Version',
    system.env.appVersion
  )
  // 设置登录信息
  setHeaderIfUnset(
    options.headers,
    'X-Auth-User-Name',
    encodeURIComponent(loginDetail.user.name || '')
  )
  setHeaderIfUnset(
    options.headers,
    'X-Auth-Member-Name',
    encodeURIComponent(loginDetail.member.name || '')
  )
  setHeaderIfUnset(options.headers, 'X-Auth-Profile-Id', loginDetail.profileId)
  return options
}

const showMsg = throttle(
  (msg: string, config: ApiConfig) => {
    if (!config.silence) {
      const errorLevel = config.errorLevel || 'warning'
      message[errorLevel](msg || '服务器异常')
    }
  },
  2000,
  { trailing: false }
)

/**
 * 调用平台内部接口时使用
 */
export const apiFetch = createRequest<ApiConfig>(() => ({
  ...system.api,
  transformRequest,
  credentials: 'include',
  onSuccess: async (res, config) => {
    const result = await parseResult(res)
    if (res.ok) {
      return result
    }
    if (res.status === 401 && !config.ignore401 && !withinWhitelist()) {
      gotoLogin()
      return
    }
    console.error('API Error:', result)
    showMsg(isString(result) ? result : result.message, config)
    return Promise.reject(result)
  },
  onFail(err, config) {
    // TODO: 记录日志
    showMsg(`网络异常: ${err.message}`, config)
    return err
  }
}))
